import { useEditor } from "@grapesjs/react";
import { Box, FormControlLabel, Grid, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import type { Trait } from "grapesjs";
import { useCallback, useMemo } from "react";

interface ILightningLanderTraitsFieldProps extends React.HTMLProps<HTMLDivElement> {
  trait: Trait;
}

export default function LightningLanderTraitsField({
  trait,
}: ILightningLanderTraitsFieldProps) {
  const editor = useEditor();
  const theme = useTheme();
  const handleChange = useCallback(
    (value: string) => {
      trait.setValue(value);
    },
    [trait],
  );

  const onChange = useCallback(
    (ev: any) => {
      handleChange(ev.target.value);
    },
    [handleChange],
  );

  const handleButtonClick = useCallback(() => {
    const command = trait.get("command");
    if (command) {
      typeof command === "string"
        ? editor.runCommand(command)
        : command(editor, trait);
    }
  }, [trait, editor]);

  const type = trait.getType();
  const defValue = trait.getDefault() || trait.attributes.placeholder;
  const value = trait.getValue();
  const valueWithDef = typeof value !== "undefined" ? value : defValue;

  const renderField = useMemo(() => {
    let inputToRender = (
      <TextField
        label={trait.getLabel()}
        placeholder={defValue}
        value={value}
        onChange={onChange}
        size="small"
        fullWidth
      />
    );

    switch (type) {
      case "select": {
        inputToRender = (
          <TextField
            select
            label={trait.getLabel()}
            placeholder={defValue}
            value={value}
            onChange={onChange}
            size="small"
            fullWidth
          >
            {trait.getOptions().map((option) => (
              <MenuItem
                key={trait.getOptionId(option)}
                value={trait.getOptionId(option)}
              >
                {trait.getOptionLabel(option)}
              </MenuItem>
            ))}
          </TextField>
        );
        break;
      }

      case "color": {
        inputToRender = (
          <TextField
            fullWidth
            label={trait.getLabel()}
            placeholder={defValue}
            value={value}
            onChange={onChange}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box
                    sx={{
                      backgroundColor: valueWithDef,
                      borderRadius: theme.shape.borderRadius,
                      width: 30,
                    }}
                  >
                    <input
                      style={{ opacity: 0, cursor: "pointer", width: 30 }}
                      type="color"
                      value={valueWithDef}
                      onChange={(ev) => handleChange(ev.target.value)}
                    />
                  </Box>
                </InputAdornment>
              ),
            }}
          />
        );
        break;
      }

      case "checkbox": {
        inputToRender = (
          <FormControlLabel
            control={
              <Checkbox
                checked={value}
                onChange={(ev) => trait.setValue(ev.target.checked)}
                size="small"
              />
            }
            label={trait.getLabel()}
          />
        );
        break;
      }

      case "button": {
        inputToRender = (
          <Button fullWidth onClick={handleButtonClick}>
            {trait.getLabel()}
          </Button>
        );
        break;
      }
    }
    return inputToRender;
  }, [
    defValue,
    handleButtonClick,
    handleChange,
    onChange,
    trait,
    type,
    value,
    valueWithDef,
  ]);

  return (
    <Grid item xs={12}>
      <div>{renderField}</div>
    </Grid>
  );
}
